import { Users } from "lucide-react"

import advisorBubbleTail from "@/assets/svg/landing/advisor-bubble-tail.svg"
import advisorRing from "@/assets/svg/landing/advisor-ring.svg"

import { ADVISORS, type AdvisorItem } from "../../config/landing-content"
import { SectionTitle } from "../section-title"

interface AstrologerSectionProps {
  onAction: () => void
}

function AdvisorCard({ advisor, onAction }: { advisor: AdvisorItem; onAction: () => void }) {
  return (
    <div className="flex flex-col rounded-[24px] bg-dark-surface p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div className="relative size-14 shrink-0">
            <img src={advisorRing} alt="" className="absolute inset-0 size-14" />
            <img
              src={advisor.avatar}
              alt=""
              className="absolute inset-1 size-12 rounded-full object-cover"
            />
          </div>
          <div className="flex min-w-0 flex-col">
            <p className="font-display text-sm font-medium text-white">{advisor.name}</p>
            <p className="truncate text-xs text-purple-light/70">{advisor.bio}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 rounded-[16px] bg-green/20 py-1.5 pl-2.5 pr-3">
          <span className="size-2 rounded-full bg-green" />
          <span className="text-xs text-green">Online</span>
        </div>
      </div>

      <div className="relative ml-1 mt-3 w-[296px] max-w-[calc(100%-4px)]">
        {/* Bubble shape — body + tail as one 10% group (like the Figma group),
            so the tail merges into the rounded corner without a double-opacity seam. */}
        <div className="pointer-events-none absolute inset-0 opacity-10">
          <div className="absolute inset-0 rounded-[16px] bg-purple-light" />
          <img
            src={advisorBubbleTail}
            alt=""
            aria-hidden
            className="absolute -bottom-px -left-1 h-[15px] w-[14px]"
          />
        </div>
        <p className="relative px-4 py-3 text-base leading-snug text-purple-light">
          {advisor.message}
        </p>
      </div>

      <button
        type="button"
        onClick={onAction}
        className="mt-4 flex h-12 w-full items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent text-base font-semibold text-white"
      >
        {advisor.cta}
      </button>
    </div>
  )
}

export function AstrologerSection({ onAction }: AstrologerSectionProps) {
  return (
    <section data-slot="astrologer-section" className="flex flex-col gap-4 px-4">
      <div className="flex flex-col gap-2">
        <SectionTitle>Ask an Astrologer Directly</SectionTitle>
        <p className="text-center text-base leading-snug text-purple-light/70">
          Choose the guidance you need and get a personal answer from an online advisor
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {ADVISORS.slice(0, 3).map((advisor) => (
          <AdvisorCard key={advisor.name} advisor={advisor} onAction={onAction} />
        ))}

        {/* Last advisor: blurred teaser, faded into the page, with a floating
            "View all advisors" pill. */}
        <div className="relative">
          <div className="relative max-h-[187px] overflow-hidden rounded-[24px]">
            <AdvisorCard advisor={ADVISORS[3]} onAction={onAction} />
            {/* Frost only the lower half — the advisor row up top stays sharp */}
            <div
              className="pointer-events-none absolute inset-0 backdrop-blur-[2px] [-webkit-mask-image:linear-gradient(to_bottom,transparent_36%,#000_52%)] [mask-image:linear-gradient(to_bottom,transparent_36%,#000_52%)]"
            />
            {/* Fade the card into the page background toward the bottom */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 top-[40%] bg-gradient-to-b from-transparent to-dark-base" />
          </div>
          <button
            type="button"
            onClick={onAction}
            className="absolute bottom-2 left-1/2 flex h-12 -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-purple-light px-8 text-base font-semibold text-dark-base"
          >
            <Users className="size-6" />
            View all advisors
          </button>
        </div>
      </div>
    </section>
  )
}
